import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChevronLeft, ChevronRight, Plus, Clock, User } from 'lucide-react';
interface Event {
  id: number;
  title: string;
  date: string;
  time: string;
  contact: string;
  type: 'meeting' | 'call' | 'followup' | 'deadline';
}
export function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<'month' | 'week' | 'day'>('month');
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    time: '',
    contact: '',
    type: 'meeting' as Event['type']
  });
  const [events, setEvents] = useState<Event[]>([{
    id: 1,
    title: 'Demo Call mit Thomas Weber',
    date: '2025-01-28',
    time: '10:00',
    contact: 'Thomas Weber',
    type: 'call'
  }, {
    id: 2,
    title: 'Follow-up Max Mustermann',
    date: '2025-01-28',
    time: '14:00',
    contact: 'Max Mustermann',
    type: 'followup'
  }, {
    id: 3,
    title: 'Meeting Anna Schmidt',
    date: '2025-01-29',
    time: '11:00',
    contact: 'Anna Schmidt',
    type: 'meeting'
  }, {
    id: 4,
    title: 'Angebot Deadline',
    date: '2025-01-30',
    time: '17:00',
    contact: 'Michael Becker',
    type: 'deadline'
  }, {
    id: 5,
    title: 'Termin Julia Fischer',
    date: '2025-01-31',
    time: '15:00',
    contact: 'Julia Fischer',
    type: 'meeting'
  }]);
  const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return {
      daysInMonth,
      startingDayOfWeek
    };
  };
  const getEventsForDate = (date: string) => {
    return events.filter(e => e.date === date);
  };
  const formatDate = (day: number) => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${month}-${dayStr}`;
  };
  const handleDayClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    setNewEvent({
      ...newEvent,
      date: dateStr
    });
    setShowAddEvent(true);
  };
  const handleSaveEvent = () => {
    if (newEvent.title && newEvent.date && newEvent.time && newEvent.contact) {
      const event: Event = {
        id: events.length + 1,
        ...newEvent
      };
      setEvents([...events, event]);
      setNewEvent({
        title: '',
        date: '',
        time: '',
        contact: '',
        type: 'meeting'
      });
      setShowAddEvent(false);
      setSelectedDate('');
    }
  };
  const handleCancelEvent = () => {
    setShowAddEvent(false);
    setSelectedDate('');
    setNewEvent({
      title: '',
      date: '',
      time: '',
      contact: '',
      type: 'meeting'
    });
  };
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };
  const typeColors: Record<string, string> = {
    meeting: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30',
    call: 'bg-orange-400/10 text-orange-500 dark:text-orange-300 border-orange-400/30',
    followup: 'bg-[#eb6641]/10 text-[#eb6641] dark:text-[#ff8a65] border-[#eb6641]/30',
    deadline: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30'
  };
  const {
    daysInMonth,
    startingDayOfWeek
  } = getDaysInMonth(currentDate);
  const days = Array.from({
    length: daysInMonth
  }, (_, i) => i + 1);
  const emptyDays = Array.from({
    length: startingDayOfWeek
  }, (_, i) => i);
  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date()).sort((a, b) => new Date(a.date + ' ' + a.time).getTime() - new Date(b.date + ' ' + b.time).getTime()).slice(0, 5);
  return <div className="p-6 space-y-6" data-id="element-124">
      <div className="flex items-center justify-between" data-id="element-125">
        <div data-id="element-126">
          <h1 className="text-3xl font-bold text-foreground" data-id="element-127">Kalender</h1>
          <p className="text-muted-foreground mt-1" data-id="element-128">
            Termine, Follow-ups und Deadlines verwalten
          </p>
        </div>
        <div className="flex gap-2" data-id="element-129">
          <Button variant="outline" size="sm" onClick={() => setView('day')} data-id="element-130">
            Tag
          </Button>
          <Button variant="outline" size="sm" onClick={() => setView('week')} data-id="element-131">
            Woche
          </Button>
          <Button variant={view === 'month' ? 'default' : 'outline'} size="sm" onClick={() => setView('month')} data-id="element-132">
            Monat
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setShowAddEvent(!showAddEvent)} data-id="element-133">
            <Plus className="h-4 w-4 mr-1" data-id="element-134" />
            Termin
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-135">
        <div className="lg:col-span-2" data-id="element-136">
          <Card data-id="element-137">
            <CardHeader data-id="element-138">
              <div className="flex items-center justify-between" data-id="element-139">
                <CardTitle className="text-lg" data-id="element-140">
                  {monthNames[currentDate.getMonth()]}{' '}
                  {currentDate.getFullYear()}
                </CardTitle>
                <div className="flex gap-2" data-id="element-141">
                  <Button variant="outline" size="icon" onClick={previousMonth} data-id="element-142">
                    <ChevronLeft className="h-4 w-4" data-id="element-143" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={nextMonth} data-id="element-144">
                    <ChevronRight className="h-4 w-4" data-id="element-145" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-146">
              <div className="grid grid-cols-7 gap-2" data-id="element-147">
                {dayNames.map(day => <div key={day} className="text-center text-xs font-medium text-muted-foreground p-2" data-id="element-148">
                    {day}
                  </div>)}
                {emptyDays.map(i => <div key={`empty-${i}`} className="p-2" data-id="element-149"></div>)}
                {days.map(day => {
                const dateStr = formatDate(day);
                const dayEvents = getEventsForDate(dateStr);
                const isToday = dateStr === new Date().toISOString().split('T')[0];
                const isSelected = dateStr === selectedDate;
                return <div key={day} onClick={() => handleDayClick(dateStr)} className={`min-h-[80px] p-2 border rounded-lg hover:bg-[#fef5ee] dark:hover:bg-white/5 transition-colors cursor-pointer ${isToday ? 'border-[#eb6641] bg-[#fef5ee] dark:bg-[#eb6641]/10' : isSelected ? 'border-[#eb6641] dark:border-[#ff8a65]' : 'border-border'}`} data-id="element-150">
                      <div className={`text-sm font-medium mb-1 ${isToday ? 'text-[#eb6641] dark:text-[#ff8a65]' : 'text-foreground'}`} data-id="element-151">
                        {day}
                      </div>
                      <div className="space-y-1" data-id="element-152">
                        {dayEvents.slice(0, 2).map(event => <div key={event.id} className={`text-xs px-1 py-0.5 rounded border truncate ${typeColors[event.type]}`} data-id="element-153">
                            {event.time} {event.title}
                          </div>)}
                        {dayEvents.length > 2 && <div className="text-xs text-muted-foreground" data-id="element-154">
                            +{dayEvents.length - 2} mehr
                          </div>}
                      </div>
                    </div>;
              })}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6" data-id="element-155">
          {showAddEvent && <Card data-id="element-156">
              <CardHeader data-id="element-157">
                <CardTitle className="text-base" data-id="element-158">Neuer Termin</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-159">
                <div className="space-y-2" data-id="element-160">
                  <Label data-id="element-161">Titel</Label>
                  <Input placeholder="z.B. Meeting mit Kunde" value={newEvent.title} onChange={e => setNewEvent({
                ...newEvent,
                title: e.target.value
              })} data-id="element-162" />
                </div>
                <div className="space-y-2" data-id="element-163">
                  <Label data-id="element-164">Datum</Label>
                  <Input type="date" value={newEvent.date} onChange={e => setNewEvent({
                ...newEvent,
                date: e.target.value
              })} data-id="element-165" />
                </div>
                <div className="space-y-2" data-id="element-166">
                  <Label data-id="element-167">Uhrzeit</Label>
                  <Input type="time" value={newEvent.time} onChange={e => setNewEvent({
                ...newEvent,
                time: e.target.value
              })} data-id="element-168" />
                </div>
                <div className="space-y-2" data-id="element-169">
                  <Label data-id="element-170">Kontakt</Label>
                  <Input placeholder="Name des Kontakts" value={newEvent.contact} onChange={e => setNewEvent({
                ...newEvent,
                contact: e.target.value
              })} data-id="element-171" />
                </div>
                <div className="space-y-2" data-id="element-172">
                  <Label data-id="element-173">Typ</Label>
                  <select className="w-full h-10 px-3 rounded-lg border border-input bg-background text-foreground text-sm" value={newEvent.type} onChange={e => setNewEvent({
                ...newEvent,
                type: e.target.value as Event['type']
              })} data-id="element-174">
                    <option value="meeting" data-id="element-175">Meeting</option>
                    <option value="call" data-id="element-176">Anruf</option>
                    <option value="followup" data-id="element-177">Follow-up</option>
                    <option value="deadline" data-id="element-178">Deadline</option>
                  </select>
                </div>
                <div className="flex gap-2" data-id="element-179">
                  <Button variant="outline" className="flex-1" onClick={handleCancelEvent} data-id="element-180">
                    Abbrechen
                  </Button>
                  <Button variant="secondary" className="flex-1" onClick={handleSaveEvent} data-id="element-181">
                    Speichern
                  </Button>
                </div>
              </CardContent>
            </Card>}

          <Card data-id="element-182">
            <CardHeader data-id="element-183">
              <CardTitle className="text-base" data-id="element-184">Anstehende Termine</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3" data-id="element-185">
              {upcomingEvents.map(event => <div key={event.id} className="p-3 border border-border rounded-lg hover:bg-[#fef5ee] dark:hover:bg-white/5 transition-colors cursor-pointer" data-id="element-186">
                  <div className="flex items-start gap-3" data-id="element-187">
                    <div className={`px-2 py-1 rounded text-xs border ${typeColors[event.type]}`} data-id="element-188">
                      {event.type === 'meeting' && 'Meeting'}
                      {event.type === 'call' && 'Call'}
                      {event.type === 'followup' && 'Follow-up'}
                      {event.type === 'deadline' && 'Deadline'}
                    </div>
                    <div className="flex-1" data-id="element-189">
                      <p className="font-medium text-sm text-foreground" data-id="element-190">
                        {event.title}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground" data-id="element-191">
                        <span className="flex items-center gap-1" data-id="element-192">
                          <Clock className="h-3 w-3" data-id="element-193" />
                          {event.date} {event.time}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-194">
                          <User className="h-3 w-3" data-id="element-195" />
                          {event.contact}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>)}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}